import { createSlice } from '@reduxjs/toolkit';
import { getPartCoverageFilters, getPRFilter } from './async';
import { FiltersState } from './types';
import { FlowTypeEnum } from 'modules/inbound/programCoverage/infrastructure/dto/JitFlowTypesDTO';
import { CoverageStatus } from 'modules/inbound/shared/domain/CoverageStatusTypes';
import {
  NEEDS_VIEW_TO_SOURCE,
  NeedsSource,
  NeedsViewKey
} from 'modules/inbound/programCoverage/domain/NeedsSource';

export const initialState: FiltersState = {
  filters: {
    family: [],
    lines: [],
    providersTier1Ids: [],
    logisticOperatorsIds: [],
    models: [],
    inbounders: [],
    flowTypes: [],
    status: [],
    prsOptions: [],
    coverageStoppedAt: { fromDate: null, toDate: null },
    quantityStock: { Min: null, Max: null }
  },
  requestStatus: '',
  requestPRStatus: '',
  selectedSubHeaderFilters: {
    selectedFamily: [],
    selectedLines: [],
    selectedProvidersTier1Ids: [],
    selectedLogisticOperatorsIds: [],
    selectedModels: [],
    selectedInbounders: [],
    selectedFlowType: { key: '', text: '' },
    selectedStatus: { key: '', text: '' },
    selectedBoardKey: { key: '', text: '' },
    selectedPRs: [],
    selectedCoverageStoppedAt: {
      key: '',
      value: { fromDate: null, toDate: null }
    },
    selectedQuantityStock: {
      key: '',
      value: { Min: null, Max: null }
    }
  },
  search: '',
  isFilterSaved: false,
  selectedNeedsView: NeedsViewKey.BESI_IFA
};

export const PART_COVERAGE_FILTERS_STATE_KEY = 'PartCoverageState';

const PartCoverageFiltersSlice = createSlice({
  name: 'partCoverageFiltersProgramCoverage',
  initialState,
  reducers: {
    updateFilterSearch: (state, action) => {
      state.search = action.payload;
    },
    updateSelectedFamily: (state, action) => {
      state.selectedSubHeaderFilters.selectedFamily = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedLines: (state, action) => {
      state.selectedSubHeaderFilters.selectedLines = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedProvidersTier1Ids: (state, action) => {
      state.selectedSubHeaderFilters.selectedProvidersTier1Ids = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedLogisticOperatorsIds: (state, action) => {
      state.selectedSubHeaderFilters.selectedLogisticOperatorsIds =
        action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedModels: (state, action) => {
      state.selectedSubHeaderFilters.selectedModels = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedInbounders: (state, action) => {
      state.selectedSubHeaderFilters.selectedInbounders = action.payload;
    },
    updateSelectedFlowType: (state, action) => {
      state.selectedSubHeaderFilters.selectedFlowType = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedStatus: (state, action) => {
      state.selectedSubHeaderFilters.selectedStatus = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedBoardKey: (state, action) => {
      state.selectedSubHeaderFilters.selectedBoardKey = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedPRs: (state, action) => {
      state.selectedSubHeaderFilters.selectedPRs = action.payload;
      state.isFilterSaved = false;
    },
    updateSelectedCoverageStoppedAt: (state, action) => {
      const fromDate = action.payload.fromDate;
      const toDate = action.payload.toDate;
      state.selectedSubHeaderFilters.selectedCoverageStoppedAt = {
        key: 'coverageStoppedAt',
        value: { fromDate: fromDate, toDate: toDate }
      };
      state.isFilterSaved = false;
    },
    updateSelectedQuantityStock: (state, action) => {
      const Max = action.payload.Max;
      const Min = action.payload.Min;
      state.selectedSubHeaderFilters.selectedQuantityStock = {
        key: 'quantityStock',
        value: { Max: Max, Min: Min }
      };
      state.isFilterSaved = false;
    },
    resetSelectedQuantityStock: (state) => {
      state.selectedSubHeaderFilters.selectedQuantityStock =
        initialState.selectedSubHeaderFilters.selectedQuantityStock;
      state.isFilterSaved = false;
    },
    resetFilters: (state) => {
      state.selectedSubHeaderFilters = initialState.selectedSubHeaderFilters;
    },
    removeFilters: (state, action) => {
      state.selectedSubHeaderFilters = initialState.selectedSubHeaderFilters;
      localStorage.removeItem(
        `${action.payload}-${PART_COVERAGE_FILTERS_STATE_KEY}`
      );
      state.isFilterSaved = false;
    },
    saveFilters: (state, action) => {
      localStorage.setItem(
        `${action.payload}-${PART_COVERAGE_FILTERS_STATE_KEY}`,
        JSON.stringify(state.selectedSubHeaderFilters)
      );
      state.isFilterSaved = true;
    },
    loadFilters: (state, action) => {
      const filters = localStorage.getItem(
        `${action.payload}-${PART_COVERAGE_FILTERS_STATE_KEY}`
      );
      if (filters) {
        const parsedFilters = JSON.parse(filters);
        state.selectedSubHeaderFilters =
          parsedFilters || initialState.selectedSubHeaderFilters;
        state.isFilterSaved = true;
      }
    },
    updateSelectedNeedsView: (state, action) => {
      state.selectedNeedsView = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPartCoverageFilters.fulfilled, (state, action) => {
      const payload = action.payload;

      state.filters = {
        ...state.filters,
        ...(payload.family !== undefined && { family: payload.family }),
        ...(payload.lines !== undefined && { lines: payload.lines }),
        ...(payload.models !== undefined && { models: payload.models }),
        ...(payload.inbounders !== undefined && {
          inbounders: payload.inbounders
        }),
        ...(payload.flowTypes !== undefined && {
          flowTypes: payload.flowTypes
        }),
        ...(payload.providersTier1Ids !== undefined && {
          providersTier1Ids: payload.providersTier1Ids
        }),
        ...(payload.logisticOperatorsIds !== undefined && {
          logisticOperatorsIds: payload.logisticOperatorsIds
        }),
        ...(payload.status !== undefined && { status: payload.status }),
        ...(payload.coverageStoppedAt !== undefined && {
          coverageStoppedAt: payload.coverageStoppedAt,
          prsOptions: state.filters.prsOptions
        })
      };

      state.requestStatus = action.meta.requestStatus;
    });

    builder.addCase(getPartCoverageFilters.pending, (state, action) => {
      state.requestStatus = action.meta.requestStatus;
    });
    builder.addCase(getPartCoverageFilters.rejected, (state, action) => {
      state.requestStatus = action.meta.requestStatus;
    });
    builder.addCase(getPRFilter.fulfilled, (state, action) => {
      state.filters = {
        ...state.filters,
        prsOptions:
          action.payload && action.payload.length > 0 ? [...action.payload] : []
      };
      state.requestPRStatus = action.meta.requestStatus;
    });
    builder.addCase(getPRFilter.pending, (state, action) => {
      state.requestPRStatus = action.meta.requestStatus;
    });
  }
});

export const getFamily = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.family;
export const getLines = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.lines;
export const getStatusTypes = (state) => {
  if (
    state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
      ?.selectedStatus?.key
  ) {
    return [CoverageStatus.COVERED, CoverageStatus.NOT_COVERED];
  }
  return state.partCoverageFiltersStateProgramCoverage.filters.status;
};

export const getProvidersTier1Ids = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.providersTier1Ids;
export const getLogisticOperatorsIds = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.logisticOperatorsIds;
export const getModels = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.models;
export const getInbounders = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.inbounders;
export const getFlowTypes = (state) =>
  state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
    .selectedFlowType.key
    ? [FlowTypeEnum.NONE, FlowTypeEnum.SYNCRO, FlowTypeEnum.NO_SYNCRO]
    : state.partCoverageFiltersStateProgramCoverage.filters.flowTypes;
export const getPRsOptions = (state) =>
  state.partCoverageFiltersStateProgramCoverage.filters.prsOptions;
export const getFilterSearch = (state) =>
  state.partCoverageFiltersStateProgramCoverage.search;
export const getSelectedSubheaderFilters = (state) => {
  return state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters;
};
export const getIsFilterSaved = (state) =>
  state.partCoverageFiltersStateProgramCoverage.isFilterSaved;
export const getHasFiltersSelected = (state) => {
  return (
    !!state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
      .selectedFlowType.key ||
    !!state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
      .selectedCoverageStoppedAt.key ||
    Object.keys(
      state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
    ).some(
      (key) =>
        state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters[
          key
        ].length
    )
  );
};

export const getCoverageStoppedAt = (state) =>
  state.partCoverageFiltersStateProgramCoverage.selectedSubHeaderFilters
    .selectedCoverageStoppedAt.value;

export const getQuantityStock = (state) => {
  let stateFilter: FiltersState = state.partCoverageFiltersStateProgramCoverage;
  return stateFilter.selectedSubHeaderFilters.selectedQuantityStock.value;
};

export const getCurrentRequestStatus = (state) => {
  return state.partCoverageFiltersStateProgramCoverage.requestStatus;
};

export const getCurrentRequestPRStatus = (state) => {
  return state.partCoverageFiltersStateProgramCoverage.requestPRStatus;
};

export const getSelectedNeedsView = (state): NeedsViewKey =>
  state.partCoverageFiltersStateProgramCoverage.selectedNeedsView;

export const getSelectedNeedsSource = (state): NeedsSource =>
  NEEDS_VIEW_TO_SOURCE[getSelectedNeedsView(state)] ?? NeedsSource.SAP;

export const {
  updateFilterSearch,
  updateSelectedFamily,
  updateSelectedLines,
  updateSelectedProvidersTier1Ids,
  updateSelectedLogisticOperatorsIds,
  updateSelectedModels,
  updateSelectedInbounders,
  updateSelectedFlowType,
  updateSelectedStatus,
  updateSelectedPRs,
  updateSelectedBoardKey,
  updateSelectedCoverageStoppedAt,
  resetFilters,
  removeFilters,
  saveFilters,
  loadFilters,
  updateSelectedQuantityStock,
  resetSelectedQuantityStock,
  updateSelectedNeedsView
} = PartCoverageFiltersSlice.actions;

export default PartCoverageFiltersSlice.reducer;
